/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';

import { getCurrentDayString } from 'ui-permit/shared-components/utils';
import { CAROLINA_SANDHILLS_ID } from 'ui-permit/src/constants';
import { AVAILABILITY_KEY_MAP_FORMAT, AVAILABILITY_FILTERS } from '../constants';
import * as constants from '../constants/shared-constants';

export const getSpecialConfigPermitsDefaultDivisionID = (
    permitID,
    availability,
    date
) => {
    if (!availability || availability.length === 0) {
        return '';
    }

    let divisionAvailability;
    switch (permitID) {
        case CAROLINA_SANDHILLS_ID:
            // at the moment of writing this, for Carolina Sandhills the divisions represent/are a single Date/Date Range
            divisionAvailability = availability.find(
                // find the quota which holds the ID of the division [as key of quota]
                (divAvailability) => divAvailability.start_date === date
            );
            break;
        // Unique Division auto selection
        default:
            if (availability.length === 1) {
                divisionAvailability = availability[0];
            }
            break;
    }

    // only way to get the ID of the division
    return Object.keys(divisionAvailability?.quota)?.[0]; // get the key of the object [division ID]
};

// NOTE: casing - consider revising across repo
export const isDayWalkupOnly = (day, props) => {
    const dayFormat = day.startOf('day').format(AVAILABILITY_KEY_MAP_FORMAT);
    return get(
        props,
        `availability.quotaTypeMaps.constantQuotaUsageByStartDate[${dayFormat}].showWalkup`,
        false
    );
};

export const isDayLotteryOnly = (day, props) => {
    const dayFormat = day.startOf('day').format(AVAILABILITY_KEY_MAP_FORMAT);
    const { divisionId } = props;
    const dayAvailability = get(props, `availability[${divisionId}].date_availability`);
    if (dayAvailability && dayAvailability[dayFormat]) {
        return dayAvailability[dayFormat].show_lottery;
    }

    // TODO: change data format (quick hotfix below) also figure out why quotaType in props is undefined
    const dayAvailabilities = get(
        props,
        `availabilities[${divisionId}].ConstantQuotaUsageByStartDate`
    );
    if (dayAvailabilities && dayAvailabilities[dayFormat]) {
        return dayAvailabilities[dayFormat].show_lottery;
    }
    // END OF TODO

    return false;
};

export const isWalkupQuotaType = (quotaType) => quotaType === 'walkup';

export const isDayManagedByLottery = (day, props) => {
    if (isDayLotteryOnly(day, props)) {
        const { dateMap } = props;
        const dayString = day.format(AVAILABILITY_KEY_MAP_FORMAT);
        if (!dateMap[dayString]) return false;
        return true;
    }
    return false;
};

export const isDayBeingBlockedByLottery = (day, props) =>
    props.blockLotteryDates && isDayManagedByLottery(day, props);

export const isDayBlockedWithReason = (day, props) => {
    const todayString = getCurrentDayString(props);
    const dayString = day.format(AVAILABILITY_KEY_MAP_FORMAT);
    if (props.allowOverbooking) {
        return constants.DAY_UNBLOCKED;
    }
    if (dayString < todayString) {
        // Day is in the past. If it's a retroactive walkup, allow it. Otherwise, block it.
        if (isWalkupQuotaType(props.quotaType)) {
            return constants.DAY_UNBLOCKED;
        }
        return constants.DAY_BLOCKED_DATE_IN_THE_PAST;
    }
    if (props.blockNonLotteryDates && !isDayManagedByLottery(day, props)) {
        return constants.DAY_BLOCKED_OUTSIDE_LOTTERY_SEASON;
    }
    if (isDayBeingBlockedByLottery(day, props)) {
        return constants.DAY_BLOCKED_BY_LOTTERY;
    }
    if (!props.dateMap) {
        return constants.DAY_BLOCKED_MISSING_QUOTA;
    }
    if (!props.dateMap[day.format(AVAILABILITY_KEY_MAP_FORMAT)]) {
        return constants.DAY_BLOCKED_NO_QUOTA;
    }
    return constants.DAY_UNBLOCKED;
};

export const isDayBlocked = (day, props) =>
    isDayBlockedWithReason(day, props) !== constants.DAY_UNBLOCKED;

export const getFilterErrors = (filterValues, groupSize) => {
    const errors = Object.keys(filterValues)
        .map((quotaFilterType) => {
            if (groupSize === 0) {
                const quotaFilterName = `${quotaFilterType.charAt(0).toUpperCase()}${quotaFilterType.slice(1)}`;
                const filter = AVAILABILITY_FILTERS().find(
                    (availabilityFilter) => quotaFilterName === availabilityFilter.type
                );
                return (
                    get(filter, 'minValue', 1) > groupSize &&
                    get(filter, 'missingInputError')
                );
            }
            return undefined;
        })
        .filter((errorMessage) => !!errorMessage);
    return errors;
};
